import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Calendar, 
  Tag, 
  ExternalLink, 
  ChevronRight, 
  Filter,
  TrendingUp,
  Bug,
  Zap,
  Wrench,
  Plus,
  Minus,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { cn } from '@/lib/utils';
import NavBar from '@/components/NavBar';
import Footer from '@/components/Footer';

interface ChangelogEntry {
  id: number;
  title: string;
  description: string;
  version: string;
  release_date: string;
  change_type: 'feature' | 'improvement' | 'bugfix' | 'breaking';
  tags: string[];
  link_url?: string;
  link_text?: string;
  created_at: string;
}

const Changelog: React.FC = () => {
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [selectedYear, setSelectedYear] = useState<string>('all');
  const { toast } = useToast();

  useEffect(() => {
    loadChangelog();
  }, []);

  const loadChangelog = async () => {
    setIsLoading(true);

    try {
      const { data, error } = await supabase
        .from('changelog_entries')
        .select('*')
        .eq('is_published', true)
        .order('release_date', { ascending: false });

      if (error) throw error;

      setEntries(data || []);
    } catch (error: any) {
      console.error('Failed to load changelog:', error);
      toast({
        title: 'Failed to load changelog',
        description: error.message || 'Please try again',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getChangeTypeIcon = (type: string) => {
    switch (type) {
      case 'feature':
        return <Plus className="h-4 w-4" />;
      case 'improvement':
        return <TrendingUp className="h-4 w-4" />;
      case 'bugfix':
        return <Bug className="h-4 w-4" />;
      case 'breaking':
        return <Zap className="h-4 w-4" />;
      default:
        return <Wrench className="h-4 w-4" />;
    }
  };

  const getChangeTypeColor = (type: string) => {
    switch (type) {
      case 'feature':
        return 'bg-green-500';
      case 'improvement':
        return 'bg-blue-500';
      case 'bugfix':
        return 'bg-red-500';
      case 'breaking':
        return 'bg-yellow-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getChangeTypeLabel = (type: string) => {
    switch (type) {
      case 'feature':
        return 'New Feature';
      case 'improvement':
        return 'Improvement';
      case 'bugfix':
        return 'Bug Fix';
      case 'breaking':
        return 'Breaking Change';
      default:
        return 'Other';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getMonthYear = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  };

  const filteredEntries = entries.filter(entry => {
    const matchesSearch = entry.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         entry.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = selectedType === 'all' || entry.change_type === selectedType;
    
    const matchesYear = selectedYear === 'all' || 
                       new Date(entry.release_date).getFullYear().toString() === selectedYear;
    
    return matchesSearch && matchesType && matchesYear;
  });

  // Group entries by month/year
  const groupedEntries = filteredEntries.reduce((acc, entry) => {
    const monthYear = getMonthYear(entry.release_date);
    if (!acc[monthYear]) {
      acc[monthYear] = [];
    }
    acc[monthYear].push(entry);
    return acc;
  }, {} as Record<string, ChangelogEntry[]>);

  const availableYears = Array.from(
    new Set(entries.map(entry => new Date(entry.release_date).getFullYear()))
  ).sort((a, b) => b - a);

  const changeTypes = [
    { value: 'all', label: 'All Changes' },
    { value: 'feature', label: 'New Features' },
    { value: 'improvement', label: 'Improvements' },
    { value: 'bugfix', label: 'Bug Fixes' },
    { value: 'breaking', label: 'Breaking Changes' },
  ];

  if (isLoading) {
    return (
      <>
        <NavBar />
        <div className="container mx-auto px-4 py-8 pt-24">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Header skeleton */}
            <div className="text-center space-y-4">
              <Skeleton className="h-10 w-48 mx-auto" />
              <Skeleton className="h-6 w-96 mx-auto" />
            </div>

            {/* Filters skeleton */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 w-48" />
              <Skeleton className="h-10 w-32" />
            </div>

            {/* Entries skeleton */}
            <div className="space-y-8">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="space-y-4">
                  <Skeleton className="h-8 w-48" />
                  <div className="space-y-4">
                    {Array.from({ length: 2 }).map((_, j) => (
                      <Skeleton key={j} className="h-32" />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <NavBar />
      <Helmet>
        <title>Changelog - Light Hunt</title>
        <meta 
          name="description" 
          content="Stay up to date with the latest features, improvements, and bug fixes for Light Hunt. See what's new in each release." 
        />
        <meta name="keywords" content="changelog, updates, releases, new features, Light Hunt" />
        <link rel="canonical" href={`${window.location.origin}/changelog`} />
        
        {/* Open Graph */}
        <meta property="og:title" content="Changelog - Light Hunt" />
        <meta property="og:description" content="Stay up to date with the latest updates and improvements for Light Hunt." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}/changelog`} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Changelog - Light Hunt" />
        <meta name="twitter:description" content="Stay up to date with the latest updates and improvements for Light Hunt." />
      </Helmet>

      <div className="container mx-auto px-4 py-8 pt-24">
        <div className="max-w-4xl mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-4">
            <h1 className="text-3xl font-bold">Changelog</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Stay up to date with the latest features, improvements, and bug fixes. 
              We continuously work to make Light Hunt better for everyone.
            </p>
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search changelog..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full"
              />
            </div>
            <Select value={selectedType} onValueChange={setSelectedType}>
              <SelectTrigger className="w-full sm:w-48">
                <SelectValue placeholder="Filter by type" />
              </SelectTrigger>
              <SelectContent>
                {changeTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-full sm:w-32">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Years</SelectItem>
                {availableYears.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Results count */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              {filteredEntries.length} {filteredEntries.length === 1 ? 'entry' : 'entries'} found
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={loadChangelog}
              disabled={isLoading}
            >
              <RefreshCw className={cn('h-4 w-4 mr-2', isLoading && 'animate-spin')} />
              Refresh
            </Button>
          </div>

          {/* Changelog Entries */}
          {Object.keys(groupedEntries).length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-semibold mb-2">No Changelog Entries</h3>
                <p className="text-muted-foreground">
                  {searchTerm || selectedType !== 'all' || selectedYear !== 'all' 
                    ? 'No entries match your current filters.'
                    : 'Check back later for updates and new features.'}
                </p>
                {(searchTerm || selectedType !== 'all' || selectedYear !== 'all') && (
                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedType('all');
                      setSelectedYear('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-12">
              {Object.entries(groupedEntries).map(([monthYear, monthEntries]) => (
                <div key={monthYear} className="space-y-6">
                  {/* Month/Year Header */}
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-semibold">{monthYear}</h2>
                    <div className="flex-1 border-t" />
                    <Badge variant="outline">{monthEntries.length} releases</Badge>
                  </div>

                  {/* Entries for this month */}
                  <div className="space-y-4">
                    {monthEntries.map((entry, index) => (
                      <Card key={entry.id} className="overflow-hidden">
                        <CardHeader>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <div className={cn(
                                  'flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-medium',
                                  getChangeTypeColor(entry.change_type)
                                )}>
                                  {getChangeTypeIcon(entry.change_type)}
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">
                                      v{entry.version}
                                    </Badge>
                                    <Badge variant="secondary" className="text-xs">
                                      {getChangeTypeLabel(entry.change_type)}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              <CardTitle className="text-lg mb-2">{entry.title}</CardTitle>
                            </div>
                            <div className="text-sm text-muted-foreground flex items-center gap-2">
                              <Calendar className="h-4 w-4" />
                              {formatDate(entry.release_date)}
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-4">
                            {/* Description */}
                            <div className="prose prose-sm max-w-none">
                              <p className="text-muted-foreground whitespace-pre-wrap">
                                {entry.description}
                              </p>
                            </div>

                            {/* Tags */}
                            {entry.tags && entry.tags.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {entry.tags.map((tag, tagIndex) => (
                                  <Badge key={tagIndex} variant="outline" className="text-xs">
                                    <Tag className="h-3 w-3 mr-1" />
                                    {tag}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            {/* Link */}
                            {entry.link_url && (
                              <div>
                                <a
                                  href={entry.link_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  {entry.link_text || 'Learn more'}
                                </a>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Footer */}
          <Card>
            <CardContent className="pt-6">
              <div className="text-center space-y-2">
                <h3 className="font-semibold">Stay in the loop</h3>
                <p className="text-sm text-muted-foreground">
                  Have suggestions or want to influence our roadmap? Share your feedback with us!
                </p>
                <div className="flex justify-center gap-4 mt-4">
                  <a 
                    href="/feedback" 
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <Plus className="h-4 w-4" />
                    Submit Feedback
                  </a>
                  <a 
                    href="/roadmap" 
                    className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
                  >
                    <ChevronRight className="h-4 w-4" />
                    View Roadmap
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Changelog;